import { useTranslation } from 'react-i18next'
import {
  formatMemberSince,
  formatUserRating,
  sellerBadgeLevelKey,
  type SellerPublicSummary,
} from '@redevicer/shared'

interface SellerBadgeProps {
  seller: SellerPublicSummary
  compact?: boolean
  className?: string
}

function sellerInitial(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed[0]?.toUpperCase() ?? '?'
}

export default function SellerBadge({ seller, compact = false, className = '' }: SellerBadgeProps) {
  const { t, i18n } = useTranslation()
  const levelKey = sellerBadgeLevelKey(seller.verification_level)
  const verified = seller.is_verified_seller || seller.verification_level >= 2
  const statusKey =
    seller.verification_level >= 3
      ? 'sellerBadge.level3'
      : verified
        ? 'sellerBadge.level2'
        : 'sellerBadge.level0'
  const statusLabel = t(statusKey)
  const memberSince = formatMemberSince(seller.member_since, i18n.language)
  const rating = formatUserRating(seller.rating_avg, seller.rating_count)
  const dealsLabel = t('sellerBadge.deals', { count: seller.completed_deals_count })

  const rootClass = [
    'seller-badge',
    `seller-badge--l${seller.verification_level}`,
    `seller-badge--${levelKey}`,
    compact ? 'seller-badge--compact' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const statusClass = `seller-badge__status seller-badge__status--${levelKey}`

  return (
    <section className={rootClass} aria-label={`${seller.display_name}, ${statusLabel}`}>
      <div className="seller-badge__bloom" aria-hidden="true" />

      <div className="seller-badge__hero">
        <div className="seller-badge__identity">
          <div className="seller-badge__avatar" aria-hidden="true">
            {sellerInitial(seller.display_name)}
          </div>
          <div className="seller-badge__intro">
            <div className="seller-badge__name-row">
              <p className={`seller-badge__name${compact ? ' seller-badge__name--compact' : ''}`}>
                {seller.display_name}
              </p>
              <span className={statusClass}>
                {verified ? (
                  <span className="seller-badge__status-icon" aria-hidden="true">
                    ✓
                  </span>
                ) : null}
                <span className="seller-badge__status-label">{statusLabel}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="seller-badge__stats">
        {memberSince ? (
          <span className="seller-badge__stat">
            <span className="seller-badge__stat-icon" aria-hidden="true">
              ◷
            </span>
            {t('sellerBadge.memberSince', { date: memberSince })}
          </span>
        ) : null}
        <span className="seller-badge__stat">
          <span className="seller-badge__stat-icon" aria-hidden="true">
            ⇄
          </span>
          {dealsLabel}
        </span>
        {rating ? (
          <span className="seller-badge__stat seller-badge__stat--rating">
            <span className="seller-badge__stat-icon" aria-hidden="true">
              ★
            </span>
            {rating}
          </span>
        ) : null}
      </div>
    </section>
  )
}
